import { useMemo, useRef } from "react";
import {
  composePillDecorators,
  type PillDecorator,
} from "@/shared/utils/variable-pill";
import { useAppSelector } from "@store";
import { createCalcPillDecorator } from "../calc/pill-decorator";
import { createDatePillDecorator } from "../date-variable/pill-decorator";
import { useCalcFormulas } from "../calc/use-calc-formulas";
import {
  getEntityRepeatableChainSelector,
  getRepeatables,
  getVersionData,
} from "@store/story/selectors";
import { createRepeatablePillDecorator } from "./pill-decorator";
import { scopeVersionDataForChain } from "./scope";

/**
 * Version data as seen from inside `entityId`'s repeatable chain: a nested
 * group's rows are lifted from ROW 1 of each enclosing group, so a text
 * element inside `team ⊃ skills` resolves `{{skills.skill}}` against
 * `team[0].skills` (see `scopeVersionDataForChain`). Returns the version data
 * unchanged (same reference) for elements outside a nested chain, so
 * non-repeatable pills resolve exactly as before.
 */
export const useScopedVersionData = (
  entityId: string,
): Record<string, unknown> | undefined => {
  const versionData = useAppSelector(getVersionData) as
    | Record<string, unknown>
    | undefined;
  // The selector factory returns a NEW memoized selector per call — keep one
  // instance per entity id or memoization is lost on every render.
  const chainSelector = useMemo(
    () => getEntityRepeatableChainSelector(entityId),
    [entityId],
  );
  const chain = useAppSelector(chainSelector);
  // `chain` is memoised on `getEntities` (= `state.story.flatContentLayers`),
  // which immer replaces on ANY entity edit anywhere in the story — not just
  // ones inside this entity's chain — and `getEntityRepeatableChain` builds a
  // fresh array every recomputation. That gives `chain` a new identity on
  // every unrelated entity mutation even when its actual keys are unchanged.
  // Key this memo on the chain's CONTENT instead of its identity, so the
  // scoped data (and everything downstream: the decorator, and the live-DOM
  // pill refresh effect in Froala.tsx that depends on it) stays referentially
  // stable while nothing in the chain actually changed. `chainRef` lets the
  // memo callback read the latest chain value without adding the
  // identity-unstable array itself as a dep.
  const chainRef = useRef(chain);
  chainRef.current = chain;
  // U+0001 is a separator unlikely to appear inside a repeatable key, so
  // distinct chains (e.g. ["ab", "c"] vs ["a", "bc"]) can't collide.
  const chainKey = chain.join("");

  return useMemo(
    () => scopeVersionDataForChain(versionData, chainRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the chain's CONTENT (chainKey), not its identity; chainRef always holds the latest chain, see comment above.
    [versionData, chainKey],
  );
};

/**
 * The `PillDecorator` a text element (Froala, the inactive render, table
 * cells) should hand to `templateToPillsHtml`/`templateToPillsInDom`: calc
 * pills keep their green Σ variant, repeatable-field pills read ROW 1 of the
 * scoped data with the blue repeatable badge, and every other token stays
 * undecorated (plain violet pill). `scopedData` comes from
 * `useScopedVersionData(entityId)` — which is where the id does its work; it
 * is taken here too so call sites read as one pair and a future per-entity
 * decoration (e.g. row focus) has somewhere to go.
 */
export const useVariablePillDecorator = (
  _entityId: string,
  scopedData: Record<string, unknown> | undefined,
  /** Custom (user-created) date variable names, so their pills resolve too. */
  customDateNames?: ReadonlySet<string>,
): PillDecorator => {
  const calcFormulas = useCalcFormulas();
  const repeatables = useAppSelector(getRepeatables);

  return useMemo(
    () =>
      composePillDecorators(
        createCalcPillDecorator(calcFormulas, scopedData),
        // Version mode only (no-op without data): the resolved, formatted date.
        createDatePillDecorator(scopedData, customDateNames),
        createRepeatablePillDecorator(repeatables, scopedData),
      ),
    [calcFormulas, repeatables, scopedData, customDateNames],
  );
};
